<template>
	<div
		class="cookie-bar"
		:class="{
			out: !isVisible,
			'border-top bg-white': isVisible,
			'fixed-bottom': (!isShopBuilder || false) && !alwaysShow
		}"
	>
		<div class="container-max" v-if="isVisible">
			<div class="row py-3" v-if="alwaysShow" :class="classes" :style="styles">
				<div class="col-12 col-md-8">
					<p v-html="text"></p>
					<div>
						<template v-for="consentGroup in consentGroups">
							<span
								v-if="consentGroup.consents.length > 0"
								class="custom-control custom-switch custom-control-appearance d-md-inline-block mr-3"
							>
								<input
									type="checkbox"
									class="custom-control-input"
									:id="_uid + '-group-' + consentGroup.key"
									:disabled="consentGroup.necessary || necessaryOnly(consentGroup)"
                                    :checked="isConsented(consentGroup.key) || consentGroup.necessary || necessaryOnly(consentGroup)"
									@change="toggleConsent(consentGroup.key)"
								/>
								<label class="custom-control-label" :for="_uid + '-group-' + consentGroup.key">
									<template v-if="consentGroup.label.length > 0">
										{{ consentGroup.label }}
									</template>
									<template v-else>
										{{ $translate("Ceres::Template.privacySettingsDefaultGroup") }}
									</template>
								</label>
							</span>
						</template>

						<a
							href="#"
							class="text-primary text-appearance d-block d-md-inline-block"
							@click.prevent.stop="isExpanded = true"
							data-testing="cookie-bar-show-more-information"
							>{{ $translate("Ceres::Template.cookieBarMoreSettings") }}</a
						>
					</div>
				</div>
				<div class="col-12 col-md-4 pt-3 pt-md-0">
					<button
						class="btn btn-default btn-block"
						@click="
							denyAll({ showNotification: true, backlink });
							close();
						"
						data-testing="cookie-bar-deny-all"
					>
						{{ $translate("Ceres::Template.cookieBarDenyAll") }}
					</button>
					<button
						class="btn btn-block"
						:class="{
							'btn-default': preloadDisplayType === 'ttdsg',
							'btn-success': (preloadDisplayType === 'amz' || preloadDisplayType === 'google' || preloadDisplayType === 'house')
						}"
						@click="
							acceptAll({ showNotification: true, backlink });
							close();
						"
						data-testing="cookie-bar-accept-all"
					>
						{{ $translate("Ceres::Template.cookieBarAcceptAll") }}
					</button>
					<button
						class="btn btn-default btn-block"
						@click="
							storeConsents({ showNotification: true, backlink });
							close();
						"
						data-testing="cookie-bar-save"
					>
						{{ $translate("Ceres::Template.cookieBarSave") }}
					</button>
				</div>
			</div>
			<div class="row py-3" v-if="isExpanded" :class="classes" :style="styles">
				<div class="col-12 mb-3">
					<privacy-settings :consent-groups="consentGroups"></privacy-settings>
				</div>
				<div class="col-12 col-md-6">
					<a
						href="#"
						class="text-primary text-appearance d-inline-block mb-3"
                        data-testing="cookie-bar-hide-more-information"
						@click.prevent.stop="isExpanded = false"
						>{{ $translate("Ceres::Template.cookieBarBack") }}</a
					>
				</div>
				<div class="col-6 col-md-3">
					<button
						class="btn btn-block btn-block btn-default"
						@click="
							denyAll({ showNotification: true, backlink });
							close();
						"
                        data-testing="cookie-bar-expanded-deny-all"
					>
						{{ $translate("Ceres::Template.cookieBarDenyAll") }}
					</button>
				</div>
				<div class="col-6 col-md-3">
					<button
						class="btn btn-block btn-block"
						:class="{
							'btn-default': preloadDisplayType === 'ttdsg',
							'btn-success': (preloadDisplayType === 'amz' || preloadDisplayType === 'google' || preloadDisplayType === 'house')
						}"
						@click="
							acceptAll({ showNotification: true, backlink });
							close();
						"
                        data-testing="cookie-bar-expanded-accept-all"
					>
						{{ $translate("Ceres::Template.cookieBarAcceptAll") }}
					</button>
				</div>
				<div class="col-6 col-md-3">
					<button
						class="btn btn-block btn-block btn-default"
						@click="
							storeConsents({ showNotification: true, backlink });
							close();
						"
                        data-testing="cookie-bar-expanded-save"
					>
						{{ $translate("Ceres::Template.cookieBarSave") }}
					</button>
				</div>
			</div>
		</div>

		<div v-else-if="enableMinimize">
			<button class="btn btn-primary btn-appearance" @click.prevent.stop="isCollapsed = false" :aria-label="$translate('Ceres::Template.cookieBarPrivacySettings')">
				<i class="fa fa-shield float-none"></i>
				<span class="d-none d-sm-inline-block">{{
					$translate("Ceres::Template.cookieBarPrivacySettings")
				}}</span>
			</button>
		</div>
	</div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { isNullOrUndefined } from "../../../ceres/helper/utils";
import UrlService from "../../services/UrlService";
import TranslationService from "../../../ceres/services/TranslationService";

export default {
	props: {
		styles: String,
		classes: String,
		consentGroups: Object,
		alwaysShow: {
			type: Boolean,
			default: false
		},
		enableMinimize: {
			type: Boolean,
			default: false
		},
		enableImplicitConsent: {
			type: Boolean,
			default: false
		},
		preloadDisplayType: {
			type: String,
			default: "ttdsg"
		},
		toHomeWhenNoBacklink: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			isCollapsed: true,
			isExpanded: false,
			scrollCounter: 0,
			backlink: ''
		};
	},

	computed: {
		isVisible() {
			return App.isShopBuilder || !this.$store.state.consents.hasResponse || !this.isCollapsed || this.alwaysShow;
		},

		isShopBuilder() {
			return App.isShopBuilder;
		},

		text() {
			const links = {
				gtc:
					'<a class="text-appearance" href="' +
					App.urls.gtc +
					'" target="_blank">' +
					this.$translate("Ceres::Template.checkoutGtc") +
					"</a>",
				cancellation:
					'<a class="text-appearance" href="' +
					App.urls.cancellationRights +
					'" target="_blank">' +
					this.$translate("Ceres::Template.checkoutCancellationRight", { hyphen: "&shy;" }) +
					"</a>",
				policy:
					'<a class="text-appearance" href="' +
					App.urls.privacyPolicy +
					'" target="_blank">' +
					this.$translate("Ceres::Template.checkoutPrivacyPolicy", {
						hyphen: "&shy;"
					}) +
					"</a>",
				legal:
					'<a class="text-appearance" href="' +
					App.urls.legalDisclosure +
					'" target="_blank">' +
					this.$translate("Ceres::Template.footerLegalDisclosure") +
					"</a>"
			};

			return this.$translate("Ceres::Template.cookieBarHintText", links);
		},

		...mapState({
			cookieBarToggle: (state) => state.consents.cookieBarToggle
		})
	},

	mounted() {
		const urlParams = UrlService.getUrlParams(document.location.search);

		if (!isNullOrUndefined(urlParams.backlink)) {
			this.backlink = decodeURIComponent(urlParams.backlink);
		} else if (this.toHomeWhenNoBacklink) {
			this.backlink = window.location.origin + (App.urls.includeLanguage ? "/" + App.language : "");
		}

		this.$nextTick(() => {
			document.getElementById("cookie-consent-accept").disabled = false;
			
			if (this.preloadDisplayType === "amz") {
				document.getElementById("cookie-consent-accept").textContent = TranslationService.translate("Eros::Template.cookieBarAmzAcceptAll");
				document.getElementById("cookie-consent-customise").textContent = TranslationService.translate("Eros::Template.cookieBarAmzCustomise");
			} else if (this.preloadDisplayType === "google") {
				document.getElementById("cookie-consent-accept").textContent = TranslationService.translate("Eros::Template.cookieBarGoogleAcceptAll");
				document.getElementById("cookie-consent-customise").textContent = TranslationService.translate("Eros::Template.cookieBarGoogleCustomise");
				document.getElementById("cookie-consent-decline").textContent = TranslationService.translate("Eros::Template.cookieBarGoogleDenyAll");				
			} else {
				document.getElementById("cookie-consent-accept").textContent = TranslationService.translate("Ceres::Template.cookieBarAcceptAll");
				document.getElementById("cookie-consent-decline").textContent = TranslationService.translate("Ceres::Template.cookieBarDenyAll");
			}


			this.initializeImplicitConsent();
		});
	},

	methods: {
		...mapActions(["storeConsents", "acceptAll", "denyAll"]),

		close() {
			this.isCollapsed = true;
			this.isExpanded = false;
		},

		isConsented(groupKey) {
			return this.$store.getters.isConsented(groupKey + ".*");
		},

		toggleConsent(groupKey) {
			this.$store.commit("toggleConsent", groupKey + ".*");
		},

        necessaryOnly(obj) {
          return obj.key === 'media' && obj.consents.every(consent => consent.necessary === true)
        },

		initializeImplicitConsent() {
			if (
				this.enableImplicitConsent &&
				App.urls.privacyPolicy !== window.location.pathname &&
				App.Eros.Config.legal.implicitCookieBarConsent &&
				!App.isShopBuilder &&
				!this.$store.state.consents.hasResponse
			) {
				document.body.addEventListener("mousedown", this.implicitConsentEvent, false);
				document.body.addEventListener("touchstart", this.implicitConsentEvent, false);
				document.addEventListener("scroll", this.implicitConsentEvent, false);
			}
		},

		implicitConsentEvent(event) {
			if (
				!isNullOrUndefined(document.querySelector("#page-header .widget-cookie-bar .cookie-bar")) ||
				!isNullOrUndefined(document.querySelector("#page-header .widget-cookie-bar #cookie-bar-preload"))) {
				const isScroll = event.type === "wheel" || event.type === "scroll";
				const blockScrollAction = this.scrollCounter < 8;

				let targetIsInBar = false;
				if (!isNullOrUndefined(document.querySelector("#page-header .widget-cookie-bar .cookie-bar"))) {
					targetIsInBar = document.querySelector("#page-header .widget-cookie-bar .cookie-bar").contains(event.target);
				}
				if (!isNullOrUndefined(document.querySelector("#page-header .widget-cookie-bar #cookie-bar-preload"))) {
					targetIsInBar = targetIsInBar || document.querySelector("#page-header .widget-cookie-bar #cookie-bar-preload").contains(event.target);
				}

				const targetIsDeclineLink =
					targetIsInBar && (event.target.nodeName === "BUTTON" || event.target.nodeName === "A");

				if (isScroll) {
					if (blockScrollAction) {
						this.scrollCounter++;
					} else {
						if (!targetIsInBar || (targetIsInBar && event.type === "wheel") || event.type === "scroll") {
							this.acceptAll({ showNotification: false, backlink: this.backlink});
							if (document.getElementById("cookie-bar-preload")) {
								document.getElementById("cookie-bar-preload").style.display = "none";
								document.getElementById("cookie-overlay").style.display = "none";
							}
							document.body.removeEventListener("mousedown", this.implicitConsentEvent, false);
							document.body.removeEventListener("touchstart", this.implicitConsentEvent, false);
							document.removeEventListener("scroll", this.implicitConsentEvent, false);
						}
					}
				} else if (!targetIsInBar || (targetIsInBar && targetIsDeclineLink)) {
					if (!targetIsDeclineLink) {
						this.acceptAll({ showNotification: false, backlink: this.backlink});
						if (document.getElementById("cookie-bar-preload")) {
							document.getElementById("cookie-bar-preload").style.display = "none";
							document.getElementById("cookie-overlay").style.display = "none";
						}
					}
					document.body.removeEventListener("mousedown", this.implicitConsentEvent, false);
					document.body.removeEventListener("touchstart", this.implicitConsentEvent, false);
					document.removeEventListener("scroll", this.implicitConsentEvent, false);
				}
			}
		}
	},

	watch: {
		cookieBarToggle(val, oldVal) {
			if (val !== oldVal) this.close();
		}
	}
};
</script>
